import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  CreditCard, Check, Crown, Loader2, 
  Shield, Zap, Star, Clock, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentPage = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [pricing, setPricing] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pricingRes, subRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/payments/pricing`),
        axios.get(`${API_URL}/api/payments/subscription`, { headers }).catch(() => ({ data: { subscription: null } })),
        axios.get(`${API_URL}/api/payments/history`, { headers }).catch(() => ({ data: { payments: [] } }))
      ]);
      setPricing(pricingRes.data);
      setSubscription(subRes.data?.subscription);
      setPayments(historyRes.data?.payments || []);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (planType) => {
    setProcessing(true);
    setSelectedPlan(planType);
    
    try {
      // Create payment intent
      const intentRes = await axios.post(`${API_URL}/api/payments/create-intent`, 
        { plan_type: planType }, 
        { headers }
      );
      
      const paymentId = intentRes.data.id;
      
      // In mock mode, we can immediately confirm
      toast.info('Traitement du paiement...');
      
      // Simulate payment processing delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Confirm payment (mock mode auto-succeeds)
      const confirmRes = await axios.post(`${API_URL}/api/payments/confirm/${paymentId}`, {}, { headers });
      
      if (confirmRes.data.status === 'succeeded') {
        toast.success('Paiement réussi !');
        fetchData(); // Refresh subscription status
      } else {
        toast.error('Paiement en attente de confirmation');
      }
    } catch (error) {
      toast.error('Erreur lors du paiement');
      console.error(error);
    } finally {
      setProcessing(false);
      setSelectedPlan(null);
    }
  };

  const formatPrice = (cents) => {
    return (cents / 100).toFixed(2).replace('.', ',') + '€';
  };

  const plans = [
    {
      id: 'patient_lifetime',
      name: 'Patient',
      price: pricing.patient_lifetime || 100,
      period: 'à vie',
      icon: <Shield className="w-6 h-6" />,
      color: 'blue',
      features: [
        'Accès illimité à la plateforme',
        'Prise de RDV en ligne',
        'Historique des consultations',
        'Notifications par email'
      ],
      popular: false
    },
    {
      id: 'patient_premium',
      name: 'Patient Premium',
      price: pricing.patient_premium || 500,
      period: '/mois',
      icon: <Crown className="w-6 h-6" />,
      color: 'amber',
      features: [
        'Tous les avantages Patient',
        'Notifications prioritaires',
        'Alertes créneaux libérés',
        'Support prioritaire',
        'Téléconsultation'
      ],
      popular: true
    },
    {
      id: 'professional_base',
      name: 'Professionnel',
      price: pricing.professional_base || 15000,
      period: '/mois',
      icon: <Star className="w-6 h-6" />,
      color: 'emerald',
      features: [
        'Dashboard professionnel complet',
        'Gestion des RDV',
        'Module Cabinet',
        'Téléconsultation illimitée',
        'Ordonnances numériques',
        'Statistiques avancées'
      ],
      popular: false
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">
            Choisissez votre formule
          </h1>
          <p className="text-slate-500 max-w-xl mx-auto">
            Accédez à toutes les fonctionnalités de drbooking avec nos offres adaptées à vos besoins
          </p>
        </div>

        {/* Current subscription */}
        {subscription && (
          <Card className="mb-8 border-2 border-green-200 bg-green-50">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Check className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-semibold text-green-800">Abonnement actif</p>
                  <p className="text-sm text-green-600">{subscription.plan_type}</p>
                </div>
              </div>
              <Badge className="bg-green-500">Actif</Badge>
            </CardContent>
          </Card>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative overflow-hidden transition-all hover:shadow-xl ${
                plan.popular ? 'border-2 border-amber-400 scale-105' : 'border border-slate-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-amber-400 text-white px-4 py-1 text-xs font-bold">
                  POPULAIRE
                </div>
              )}
              
              <CardHeader className="text-center pb-4">
                <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-3 ${
                  plan.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                  plan.color === 'amber' ? 'bg-amber-100 text-amber-600' :
                  'bg-emerald-100 text-emerald-600'
                }`}>
                  {plan.icon}
                </div>
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <div className="mt-2">
                  <span className="text-4xl font-bold text-slate-800">{formatPrice(plan.price)}</span>
                  <span className="text-slate-500 text-sm ml-1">{plan.period}</span>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <ul className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  className={`w-full mt-4 ${
                    plan.color === 'blue' ? 'bg-blue-500 hover:bg-blue-600' :
                    plan.color === 'amber' ? 'bg-amber-500 hover:bg-amber-600' :
                    'bg-emerald-500 hover:bg-emerald-600'
                  }`}
                  onClick={() => handlePayment(plan.id)}
                  disabled={processing || subscription?.plan_type === plan.id}
                >
                  {processing && selectedPlan === plan.id ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  {subscription?.plan_type === plan.id ? 'Actif' : 'Choisir cette offre'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Payment History */}
        {payments.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Historique des paiements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {payments.map((payment) => (
                  <div 
                    key={payment.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{payment.description}</p>
                      <p className="text-sm text-slate-500">
                        {new Date(payment.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{formatPrice(payment.amount_cents)}</p>
                      <Badge variant={payment.status === 'succeeded' ? 'default' : 'secondary'}>
                        {payment.status === 'succeeded' ? 'Payé' : payment.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Security notice */}
        <div className="mt-8 text-center text-sm text-slate-400">
          <Shield className="w-4 h-4 inline mr-1" />
          Paiements sécurisés • Données chiffrées • Conforme RGPD
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
